import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { confirmarCodigo, reenviarCodigo } from "../services/api";
import { AvisoEmail, useCampoEmail } from "./AvisoEmail";

/**
 * Popup de cadastro SEGMENTADO de empresa: abre por cima da página (sem trocar de tela),
 * mostra SÓ os campos de empresa e, após cadastrar, pede o CÓDIGO de 6 dígitos enviado por
 * e-mail (com aviso de spam e opção de reenviar / confirmar depois). Não altera a LoginPage.
 */
export default function CadastroEmpresaModal({ open, onClose, empresaNome = "" }) {
  const { registrar } = useAuth();
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState("form");            // "form" | "codigo" | "ok"
  const [form, setForm] = useState({ nome: "", nomeEmpresa: empresaNome, email: "", senha: "" });
  const [erro, setErro] = useState(null);
  const campoEmail = useCampoEmail(form.email, (v) => setForm((f) => ({ ...f, email: v })));
  const [carregando, setCarregando] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [codErro, setCodErro] = useState(null);
  const [codLoading, setCodLoading] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [verificado, setVerificado] = useState(false);   // confirmou pelo código (vs. "confirmar depois")

  // Pré-preenche o nome quando o popup é aberto a partir da página de uma empresa específica
  // (recrutador que caiu em /empresas/{id} pela busca → "É o RH desta empresa?").
  useEffect(() => { if (open && empresaNome) setForm((f) => ({ ...f, nomeEmpresa: empresaNome })); }, [open, empresaNome]);

  // Fecha no Esc + trava o scroll do fundo enquanto aberto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  const set = (c) => (e) => setForm({ ...form, [c]: e.target.value });
  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary outline-none";

  const enviar = async (e) => {
    e.preventDefault();
    setErro(null);
    setCarregando(true);
    try {
      await registrar({ nome: form.nome, email: form.email, senha: form.senha, tipo: 1, nomeEmpresa: form.nomeEmpresa, whatsapp: null });
      setEtapa("codigo"); // conta criada (auto-login feito) → confirmar e-mail por código
    } catch (err) {
      setErro(err.message || "Não foi possível criar a conta.");
    } finally {
      setCarregando(false);
    }
  };

  const confirmar = async (e) => {
    e.preventDefault();
    setCodErro(null);
    setCodLoading(true);
    try {
      await confirmarCodigo(form.email, codigo);
      setVerificado(true);
      setEtapa("ok");
    } catch (err) {
      setCodErro(err.message || "Código inválido.");
    } finally {
      setCodLoading(false);
    }
  };

  const reenviar = async () => {
    setCodErro(null);
    try { await reenviarCodigo(form.email); setReenviado(true); } catch { setReenviado(true); }
  };

  const fechar = () => { navigate("/painel"); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Cadastrar empresa"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Fechar"
          className="float-right -mt-1 -mr-1 text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>

        {/* ---- Etapa 1: formulário de empresa ---- */}
        {etapa === "form" && (
          <>
            <h2 className="text-xl font-bold text-text">🏢 {empresaNome ? `Cadastre-se como RH da ${empresaNome}` : "Conta de empresa / recrutador"}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {empresaNome
                ? "Crie a conta para responder aos relatos desta empresa, publicar vagas e construir sua reputação no eaiRH."
                : "Crie sua conta de empresa ou recrutador para responder aos relatos, publicar vagas, buscar candidatos e construir sua reputação no eaiRH."}
            </p>
            {erro && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm">{erro}</div>}
            <form onSubmit={enviar} className="space-y-4">
              <input className={inputCls} autoComplete="name" aria-label="Seu nome (responsável)" placeholder="Seu nome (responsável)" value={form.nome} onChange={set("nome")} required />
              {empresaNome ? (
                <div>
                  <input className={`${inputCls} bg-gray-100 text-gray-700 cursor-not-allowed`} aria-label="Nome da empresa" value={form.nomeEmpresa} readOnly />
                  <p className="text-[11px] text-gray-400 mt-1">Você está se cadastrando como RH da <strong>{empresaNome}</strong>.</p>
                </div>
              ) : (
                <input className={inputCls} aria-label="Nome da empresa" placeholder="Nome da empresa" value={form.nomeEmpresa} onChange={set("nomeEmpresa")} required />
              )}
              <input type="email" className={inputCls} autoComplete="email" aria-label="E-mail corporativo" placeholder="E-mail corporativo (ex: nome@suaempresa.com.br)" value={form.email}
                onChange={(e) => { set("email")(e); campoEmail.aoDigitar(); }} onBlur={campoEmail.aoSair} required />
              <AvisoEmail {...campoEmail.aviso} />
              <p className="text-[11px] text-primary -mt-1.5 leading-relaxed">
                📧 Use o e-mail <strong>corporativo</strong> da empresa. Gmail, Hotmail e Outlook não são aceitos para conta de empresa, e o cadastro passa por aprovação.
              </p>
              <input type="password" className={inputCls} autoComplete="new-password" aria-label="Senha" placeholder="Senha" value={form.senha} onChange={set("senha")} minLength={6} required />
              <button type="submit" disabled={carregando}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg">
                {carregando ? "Aguarde..." : "Criar conta"}
              </button>
              <p className="text-[11px] text-gray-400 mt-1 text-center leading-relaxed">
                🔒 Conexão segura (HTTPS). Ao criar conta, você concorda com os <Link to="/termos" className="text-primary hover:underline">Termos</Link> e a <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>.
              </p>
            </form>
            <p className="text-sm mt-3 text-center text-gray-600">
              Já tem conta? <Link to="/entrar" className="text-primary font-semibold hover:underline" onClick={onClose}>Entrar</Link>
            </p>
          </>
        )}

        {/* ---- Etapa 2: confirmar por código ---- */}
        {etapa === "codigo" && (
          <>
            <h2 className="text-xl font-bold text-text">✉️ Confirme seu e-mail</h2>
            <p className="text-sm text-gray-600 mt-1">
              Enviamos um <strong>código de 6 dígitos</strong> para <strong className="break-all">{form.email}</strong>. Digite-o abaixo para confirmar.
            </p>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-3 mb-4">
              📬 <strong>Não chegou em 1–2 minutos?</strong> Dê uma olhada na caixa de <strong>spam / lixo eletrônico</strong> (e em "Promoções", no Gmail) e marque como <strong>“não é spam”</strong>.
            </div>
            {codErro && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm">{codErro}</div>}
            <form onSubmit={confirmar} className="space-y-3">
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-3 text-center text-2xl font-bold tracking-[0.5em] focus:ring-2 focus:ring-primary outline-none"
                inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="Código de 6 dígitos" placeholder="••••••"
                value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus required />
              <button type="submit" disabled={codLoading || codigo.length !== 6}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg">
                {codLoading ? "Confirmando..." : "Confirmar e-mail"}
              </button>
            </form>
            <div className="mt-3 flex items-center justify-between text-sm">
              <button type="button" onClick={reenviar} className="text-primary font-semibold hover:underline">
                {reenviado ? "Código reenviado ✓" : "Reenviar código"}
              </button>
              <button type="button" onClick={() => { setVerificado(false); setEtapa("ok"); }} className="text-gray-500 hover:underline">
                Confirmar depois
              </button>
            </div>
          </>
        )}

        {/* ---- Etapa 3: sucesso ---- */}
        {etapa === "ok" && (
          <div className="text-center">
            <div className="text-4xl mb-1">{verificado ? "✅" : "🎉"}</div>
            <h2 className="text-lg font-bold text-text">{verificado ? "E-mail confirmado!" : "Empresa cadastrada!"}</h2>
            <p className="text-sm text-gray-600 mt-2">
              {verificado
                ? <>Tudo certo com seu e-mail. </>
                : <>Enviamos um código para <strong className="break-all">{form.email}</strong> — você pode confirmar depois em “Minha conta”. </>}
              O cadastro de empresa passa por uma <strong>aprovação</strong> — você será avisado(a) quando estiver liberado para responder relatos e publicar vagas.
            </p>
            <button onClick={fechar} className="mt-4 w-full rounded-xl bg-primary text-white font-semibold py-2.5 hover:opacity-90">
              Ir para o painel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
